import { useState, useEffect,useCallback } from "react";
import { useApi } from "../context/useApi";
import {
  FaUser,
  FaEdit,
  FaCheck,
  FaSearch,
  FaFilter,
  FaCrown,
  FaShieldAlt,
  FaPhone,
  FaEnvelope,
  FaCalendar,
  FaUsers,
  FaWallet,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const UsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [editingUserId, setEditingUserId] = useState(null);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // table or details
  const { apiFetch } = useApi();


  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSubscription, setFilterSubscription] = useState("all");
  const [filterLevel, setFilterLevel] = useState("all");
  const [filterVerified, setFilterVerified] = useState("all");
  const [filterGender, setFilterGender] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };
    const API_BASE = "https://admin-backend-fbuuh6fo3q-el.a.run.app/users";
 

const fetchUsers = useCallback(async () => {
  setIsLoading(true);
  try {
    const res = await apiFetch(`${API_BASE}/get-users`);
    const data = await res.json();
    if (res.ok) setUsers(data.users || []);
    else showToast(data.error || "Failed to fetch users", "error");
  } catch (err) {
    console.error(err);
    showToast("Error fetching users", "error");
  } finally {
    setIsLoading(false);
  }
}, [apiFetch]);

useEffect(() => {
  fetchUsers();
}, [fetchUsers]);

  const handleEditClick = (user) => {
    setEditingUserId(user.user_id);
    setEditData({
      user_level: user.user_level,
      subscription_level: user.subscription_level,
      score: user.score,
      is_verified: user.is_verified,
    });
  };

  const handleCancel = () => {
    setEditingUserId(null);
    setEditData({});
  };

const handleSave = async (userId) => {
  setIsSaving(userId);
  try {
    const res = await apiFetch(`${API_BASE}/update-user/${userId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editData),
    });
    const data = await res.json();
    if (res.ok) {
      showToast("User updated successfully!");
      fetchUsers();
      setEditingUserId(null);
    } else showToast(data.error || "Failed to update user", "error");
  } catch (err) {
    console.error(err);
    showToast("Failed to update user", "error");
  } finally {
    setIsSaving(null);
  }
};

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setViewMode("details");
  };

  const handleBackToList = () => {
    setSelectedUser(null);
    setViewMode("table");
    setEditingUserId(null);
  };

  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.mobile_number || "").includes(searchTerm);

    const matchesSubscription =
      filterSubscription === "all" ||
      u.subscription_level === filterSubscription;
    const matchesLevel = filterLevel === "all" || u.user_level === filterLevel;
    const matchesVerified =
      filterVerified === "all" ||
      (filterVerified === "verified" && u.is_verified) ||
      (filterVerified === "not_verified" && !u.is_verified);
    const matchesGender =
      filterGender === "all" ||
      u.gender.toLowerCase() === filterGender.toLowerCase();

    return (
      matchesSearch &&
      matchesSubscription &&
      matchesLevel &&
      matchesVerified &&
      matchesGender
    );
  });

  const getSubscriptionBadge = (level) => {
    return level === "pro" ? (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-semibold rounded-full">
        <FaCrown size={10} /> PRO
      </span>
    ) : (
      <span className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
        FREE
      </span>
    );
  };

  const getLevelBadge = (level) => {
    const colors = {
      basic: "bg-blue-100 text-blue-700",
      intermediate: "bg-purple-100 text-purple-700",
      advanced: "bg-green-100 text-green-700",
    };
    return (
      <span
        className={`px-2.5 py-1 text-xs font-medium rounded-full ${
          colors[level] || colors.basic
        }`}
      >
        {level.charAt(0).toUpperCase() + level.slice(1)}
      </span>
    );
  };

  if (viewMode === "details" && selectedUser) {
    const isEditing = editingUserId === selectedUser.user_id;

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b shadow-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleBackToList}
                  className="text-gray-600 hover:text-gray-900 font-medium text-sm"
                >
                  ← Users
                </button>
                <div className="h-6 w-px bg-gray-300"></div>
                <div className="flex items-center gap-3">
                  <div>
                    <h1 className="text-xl font-semibold text-gray-900">
                      {selectedUser.name}
                    </h1>
                    <p className="text-xs text-gray-500 mt-0.5">
                      ID: {selectedUser.user_id}
                    </p>
                  </div>
                  {getSubscriptionBadge(selectedUser.subscription_level)}
                  {selectedUser.is_verified && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      <FaShieldAlt size={10} /> Verified
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {!isEditing ? (
                  <button
                    onClick={() => handleEditClick(selectedUser)}
                    className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2"
                  >
                    <FaEdit size={14} />
                    Edit User
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      disabled={isSaving === selectedUser.user_id}
                      className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleSave(selectedUser.user_id)}
                      disabled={isSaving === selectedUser.user_id}
                      className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                    >
                      <FaCheck size={14} />
                      {isSaving === selectedUser.user_id
                        ? "Saving..."
                        : "Save Changes"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* User Details Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Left Column */}
            <div className="col-span-4 space-y-4">
              {/* Profile Card */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex flex-col items-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center text-white text-3xl font-bold mb-4">
                    {selectedUser.name.charAt(0)}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedUser.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedUser.email}
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    {getSubscriptionBadge(selectedUser.subscription_level)}
                    {getLevelBadge(selectedUser.user_level)}
                  </div>
                </div>
              </div>

              {/* Stats Card */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">
                  Statistics
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FaWallet size={14} className="text-gray-400" />
                      Wallet Points
                    </div>
                    <span className="font-semibold text-gray-900">
                      {selectedUser.wallet_points.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FaUsers size={14} className="text-gray-400" />
                      Referrals
                    </div>
                    <span className="font-semibold text-gray-900">
                      {selectedUser.referral_count}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">Score</div>
                    <span className="font-semibold text-gray-900">
                      {selectedUser.score}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="col-span-8 space-y-6">
              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">
                  Contact Information
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      <FaEnvelope className="inline mr-1" size={12} /> Email
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedUser.email}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      <FaPhone className="inline mr-1" size={12} /> Mobile
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedUser.mobile_number}
                    </p>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">
                  Personal Information
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      <FaCalendar className="inline mr-1" size={12} /> Date of
                      Birth
                    </label>
                    <p className="text-sm text-gray-900">{selectedUser.dob}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Gender
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedUser.gender}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Aadhar (Last 6)
                    </label>
                    <p className="text-sm text-gray-900 font-mono">
                      {selectedUser.aadhar_last6}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Member Since
                    </label>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedUser.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Account Settings */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">
                  Account Settings
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  {isEditing ? (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          User Level
                        </label>
                        <select
                          value={editData.user_level}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              user_level: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        >
                          <option value="basic">Basic</option>
                          <option value="intermediate">Intermediate</option>
                          <option value="advanced">Advanced</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Subscription
                        </label>
                        <select
                          value={editData.subscription_level}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              subscription_level: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        >
                          <option value="free">Free</option>
                          <option value="pro">Pro</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Score
                        </label>
                        <input
                          type="number"
                          value={editData.score || ""}
                          onChange={(e) =>
                            setEditData({ ...editData, score: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Verified Status
                        </label>
                        <select
                          value={editData.is_verified ? "true" : "false"}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              is_verified: e.target.value === "true",
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        >
                          <option value="true">Verified</option>
                          <option value="false">Not Verified</option>
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          User Level
                        </label>
                        <div>{getLevelBadge(selectedUser.user_level)}</div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Subscription
                        </label>
                        <div>
                          {getSubscriptionBadge(
                            selectedUser.subscription_level
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Score
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedUser.score}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Verification
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedUser.is_verified
                            ? "✓ Verified"
                            : "✗ Not Verified"}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Referral Information */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">
                  Referral Information
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Referral Code
                    </label>
                    <p className="text-sm text-gray-900 font-mono">
                      {selectedUser.referral_code}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Referred By
                    </label>
                    <p className="text-sm text-gray-900 font-mono">
                      {selectedUser.referred_by || "—"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Total Referrals
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedUser.referral_count}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Toast */}
        {toast.message && (
          <div
            className={`fixed bottom-6 right-6 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
              toast.type === "success" ? "bg-gray-900" : "bg-red-600"
            }`}
          >
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  // Table View
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage and monitor all registered users • {filteredUsers.length} total
        </p>
      </div>

      {/* Filters Card */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 relative">
            <FaSearch
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={14}
            />
            <input
              type="text"
              placeholder="Search by name, email, or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium"
          >
            <FaFilter size={12} />
            Filters
            {showFilters ? (
              <FaChevronUp size={10} />
            ) : (
              <FaChevronDown size={10} />
            )}
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-4 gap-3 pt-3 border-t">
            <select
              value={filterSubscription}
              onChange={(e) => setFilterSubscription(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Subscriptions</option>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
            </select>

            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Levels</option>
              <option value="basic">Basic</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>

            <select
              value={filterVerified}
              onChange={(e) => setFilterVerified(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Verification</option>
              <option value="verified">Verified</option>
              <option value="not_verified">Not Verified</option>
            </select>

            <select
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Genders</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
        )}
      </div>

      {/* Users Table */}
{isLoading ? (
  <div className="space-y-4">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="border-2 border-gray-200 rounded-lg p-4 animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 border-2 border-gray-300 rounded-full"></div>
          <div className="flex-1 space-y-2">
            <div className="h-5 w-48 border border-gray-300 rounded"></div>
            <div className="h-4 w-64 border border-gray-300 rounded"></div>
          </div>
          <div className="flex gap-2">
            <div className="h-8 w-20 border border-gray-300 rounded"></div>
            <div className="h-8 w-8 border border-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
) : filteredUsers.length === 0 ? (
  <div className="border-2 border-dashed border-gray-300 rounded-lg p-16 text-center">
    <div className="inline-block p-4 border-2 border-gray-300 rounded-full mb-4">
      <FaUser className="text-gray-400" size={48} />
    </div>
    <p className="text-gray-600 font-medium mb-1">No users found</p>
    <p className="text-sm text-gray-500">Try adjusting your filters or search criteria</p>
  </div>
)  : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase">
                    User
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase">
                    Contact
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase">
                    Level
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase">
                    Subscription
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase">
                    Score
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase">
                    Status
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {user.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {user.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {user.user_id}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-900">{user.email}</p>
                      <p className="text-xs text-gray-500">
                        {user.mobile_number}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      {getLevelBadge(user.user_level)}
                    </td>
                    <td className="py-3 px-4">
                      {getSubscriptionBadge(user.subscription_level)}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm font-semibold text-gray-900">
                        {user.score}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {user.is_verified ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          <FaShieldAlt size={10} /> Verified
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                          Unverified
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => handleViewDetails(user)}
                        className="text-gray-600 hover:text-gray-900 font-medium text-sm"
                      >
                        View Details →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.message && (
        <div
          className={`fixed bottom-6 right-6 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
            toast.type === "success" ? "bg-gray-900" : "bg-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default UsersPanel;


